import React, {useEffect, useState, useRef} from 'react';
import {BsPause, BsPlay} from 'react-icons/bs';
import {Button, SolarPallet, Toggle} from '../../../../components';
import {Button as AntdButton, Flex} from 'antd';
import {Sun} from '@phosphor-icons/react';
import {useViewerStore} from "../../../../store/store";
import {useCurrentUser} from "../../../../contexts/UserContext";
import SliderInput from "../../../../components/Pure-UI-Components/SliderInput/SliderInput";
import { GetRoundNum } from '../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common';
import { AlertType } from '../../../../@types/enums';
import { flagToSunPositionVersion, hasPreCutoffSunPositionHistory } from "../../../../helpers/sunPositionVersion";
import SunPositionVersionModal from "../../../../components/Layout-Components/Editor-Layout/Modal/Modal-Content/SunPositionVersionModal";

const rangeDate = {min: 1, max: 365, step: 1};
const rangeTime = {min: 0, max: 24, step: 0.1};

const IrradianceProperties: React.FC = () => {
    const sunPathControlsContainerRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for interval ID
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const {user} = useCurrentUser();
    const {selectedDesignIndex, designList, useDesignCenteredSunPosition} = useViewerStore();

    const [playing, setPlay] = useState(false);
    const sunDate = useViewerStore(state => state.sunDate);
    const [sunTime, setSunTime] = useState(0);

    useEffect(() => {
        sceneViewer?.hideObject('ModuleTexture', true);

        return () => {
            sceneViewer?.hideObject('ModuleTexture', false);
        };
    }, [sceneViewer]);

    const isSunSimulationRunning = useViewerStore((state) => state.isSunSimulationRunning);
    const showSunPathLine = useViewerStore((state) => state.showSunPathLine);

    useEffect(() => {
        useViewerStore.setState({
            isSunSimulationRunning: false,
            showSunPathLine: false
        });
    }, []);

    const onChangeSunSimulation = () => {
        if (isSunSimulationRunning) { //toggling off simulation
            // const active = btnSunTimePlay.classList.contains('active');
            if (playing) playOrPause(); // Pause if playing simulation
            // Clear the interval if it's running
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null; // Reset the interval ID
            }
        }
        useViewerStore.setState({isSunSimulationRunning: !isSunSimulationRunning});

        sunPathControlsContainerRef.current?.classList.toggle('disabled-area');

        sceneViewer.hideObject('SunVector', !isSunSimulationRunning)
    }

    const onChangeSunPathLine = () => {
        useViewerStore.setState({showSunPathLine: !showSunPathLine});
        sceneViewer.showSunPathLine(!showSunPathLine);
    }

    const playOrPause = () => {
        const btnSunTimePlay = document.getElementsByClassName('btn-sun-time-play')[0];
        const btnPlay = document.getElementsByClassName('btn-sun-play')[0];
        const btnPause = document.getElementsByClassName('btn-sun-pause')[0];

        if (playing) { // On Pause
            btnSunTimePlay.classList.remove('active');
            btnPlay.classList.remove('inactive');
            btnPause.classList.add('inactive');
            setPlay(false);
            // Clear the interval when paused
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null; // Reset the interval ID
            }
        } else { // On Play
            btnSunTimePlay.classList.add('active');
            btnPause.classList.remove('inactive');
            btnPlay.classList.add('inactive');
            setPlay(true);
            startTimer();
        }
    };

    function startTimer() {
        let sunDateValue = sunDate;
        let sunTimeValue = sunTime;

        // Define smaller time steps (e.g., 0.1 hour increments)
        const timeStep = 0.1;
        intervalRef.current = setInterval(() => {
            sunTimeValue += timeStep;
            if (sunTimeValue >= 24) {
                sunTimeValue = 0;
                sunDateValue += 1;
            }
            if (sunDateValue > 365) {
                sunDateValue = 1;  // Reset to 1 instead of 0 for a typical 1-based calendar
            }
            useViewerStore.setState({sunDate: sunDateValue});
            setSunTime(GetRoundNum(sunTimeValue, 1));
        }, 40); // Update every 40ms for smoother animation

    }

    useEffect(() => {
        sceneViewer.updateSunPathDate(sunDate)
    }, [sunDate]);

    useEffect(() => {
        sceneViewer.updateSunPosTime(sunTime);
    }, [sunTime]);

    const onChangeSunDate = (value: number) => {
        const sunDateValue = value;
        useViewerStore.setState({sunDate: sunDateValue});

    }

    const onChangeSunTime = (value: number) => {
        const sunTimeValue = value;
        setSunTime(sunTimeValue);
    }

    const isOpenFinalizedDesign = selectedDesignIndex >= 0;
    const appliedSunPositionLabel =
        flagToSunPositionVersion(useDesignCenteredSunPosition) === "updated"
            ? "Updated Sun Position"
            : "Legacy Sun Position";
    const showSunPositionOverride =
        !!user &&
        (user.application_role_name === "Admin" || user.application_role_name === "RSA") &&
        isOpenFinalizedDesign &&
        hasPreCutoffSunPositionHistory(designList);

    const openSunPositionModal = () => {
        sceneViewer.viewerAlert({
            show: true,
            title: "Choose Sun Position",
            message: <SunPositionVersionModal />,
            messageType: AlertType.info,
            isModal: true,
        });
    };

    return (
        <div className='tab__inner'>
            <div className='tab__title'>
                <span className='tab__title--text tab__title--flex'>Irradiance</span>
            </div>
            <div className='tab__column'>
                <div className='tab__flex justify-between mb-2'>
                    <SolarPallet/>
                </div>
            </div>

            {isOpenFinalizedDesign &&
                <div className='tab__column'>
                    <span
                        className='tab__column--subtitle mb-0'
                        style={{display: 'block', marginTop: 0, marginBottom: 12, whiteSpace: 'nowrap'}}
                    >
                        Sun Position Applied: <b>{appliedSunPositionLabel}</b>
                    </span>
                    {showSunPositionOverride &&
                        <>
                            <br/>
                            <Flex align='center' justify='center' gap={15}>
                            <AntdButton
                                icon={<Sun size={18}/>}
                                type='default'
                                size='middle'
                                className={"full-width"}
                                onClick={openSunPositionModal}
                            >Toggle Sun Position</AntdButton>
                        </Flex>
                        </>
                    }
                </div>
            }

            <div className='tab__column'>
                <div className="tab__flex justify-between">
                    <span className='tab__column--subtitle mb-0'>Sun Path Simulation</span>
                    <Toggle
                        on={isSunSimulationRunning}
                        onChange={onChangeSunSimulation}
                    />
                </div>
                <br/>
                <div
                    ref={sunPathControlsContainerRef}
                    className='tab__data disabled-area' id='settingSunSimulation'>
                    <div className="tab__flex justify-between">
                        <span className='tab__column--subtitle mb-0'>Display Sun Path Line</span>
                        <Toggle
                            on={showSunPathLine}
                            onChange={onChangeSunPathLine}
                            property='SunPathLine'
                        />
                    </div>

                    <span id='sunDate' className="tab__column--subtitle">Select Date</span>

                    <SliderInput
                        settings={rangeDate}
                        value={sunDate}
                        onChange={onChangeSunDate}
                        onChangeComplete={onChangeSunDate}
                        disabled={playing}
                    />

                    <span id='labelSunTime' className="tab__column--subtitle">Select Time</span>

                    <SliderInput
                        settings={rangeTime}
                        value={sunTime}
                        onChange={onChangeSunTime}
                        onChangeComplete={onChangeSunTime}
                        disabled={playing}
                    />

                    <Button title={"Play Simulation"} variant='btn-clear' size='btn-resize'
                            className='tab__play btn-sun-time-play' onClick={playOrPause}><BsPlay
                        className='btn-sun-play'/><BsPause className='btn-sun-pause inactive'/></Button>

                    <small className={"theme-based-text-color"}>
                        <b>Note:</b> Sun path simulation is for current year and location.
                    </small>
                </div>
            </div>
        </div>
    );
}

export default IrradianceProperties;